import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";

import ConfirmAction from "./ConfirmAction";
import { useToolStyles } from "./styles";
import { adminToolsApi, parseId } from "../../utils/adminToolsApi";

const UnfinalizeEventTool = () => {
  const classes = useToolStyles();
  const [eventIdInput, setEventIdInput] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const typedEventId = parseId(eventIdInput);

  const showPreview = async () => {
    setLoading(true);
    try {
      setResult(await adminToolsApi.previewUnfinalize(typedEventId));
    } catch (error) {
      setResult(null);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const unfinalize = async () => {
    setBusy(true);
    try {
      const done = await adminToolsApi.unfinalize(result.eventId);
      setResult(done);
      setConfirmOpen(false);
      toast.success(`Event ${done.eventId} un-finalized`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper
      className={classes.section}
      component="section"
      aria-labelledby="unfinalize-tool"
    >
      <Typography
        id="unfinalize-tool"
        component="h2"
        className={classes.sectionTitle}
      >
        Un-finalize an event
      </Typography>
      <Typography className={classes.sectionText}>
        Puts a finalized event, with its combined and split siblings, back to
        just before Finalize was pressed so it can be finalized again: events
        return to in progress and their results are deleted. Championship events
        are refused. Emails already sent are not recalled, and finalizing again
        sends them again. Preview first.
      </Typography>

      <Box className={classes.actions}>
        <TextField
          label="Event ID"
          variant="outlined"
          size="small"
          className={classes.idField}
          value={eventIdInput}
          onChange={(e) => {
            setEventIdInput(e.target.value);
            setResult(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && typedEventId) showPreview();
          }}
          inputProps={{ inputMode: "numeric" }}
        />
        <Button
          variant="outlined"
          className={classes.touch}
          disabled={!typedEventId || loading}
          onClick={showPreview}
        >
          Preview
        </Button>
        {loading && <CircularProgress size={20} />}
      </Box>

      {result && (
        <Box>
          <Box className={classes.summary}>
            {result.dryRun
              ? `Un-finalizing event ${result.eventId} would:`
              : `Event ${result.eventId} was un-finalized:`}
          </Box>
          <TableContainer>
            <Table size="small" aria-label="Un-finalize steps">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>Step</TableCell>
                  <TableCell className={classes.headerCell}>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(result.steps || []).map((step, index) => (
                  // Steps have no id; their position is stable for one response.
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={index}>
                    <TableCell className={classes.cell}>{step.item}</TableCell>
                    <TableCell className={classes.cell}>
                      {step.detail}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {result.dryRun && (
            <Box marginTop="12px">
              <Button
                variant="contained"
                color="primary"
                className={classes.touch}
                onClick={() => setConfirmOpen(true)}
              >
                Un-finalize event
              </Button>
            </Box>
          )}
        </Box>
      )}

      <ConfirmAction
        open={confirmOpen}
        title="Un-finalize this event?"
        text={
          `Event ${
            result?.eventId ?? ""
          } and its combined and split siblings go back ` +
          "to in progress, and their results (with result feed items and likes) are " +
          "deleted, as listed in the preview. Emails already sent are not recalled; " +
          "finalizing again sends them again."
        }
        confirmLabel="Un-finalize"
        busy={busy}
        onConfirm={unfinalize}
        onCancel={() => setConfirmOpen(false)}
      />
    </Paper>
  );
};

export default UnfinalizeEventTool;
